import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
/** @jsx jsx */
import { Button, jsx, Inline } from '@front10/base-elements';
import LocationSelector from '../LocationSelector';
import Column from '../../elements/Col';
import Text from '../../elements/Text';
import Box from '../../elements/Box';
import PropertyCode from '../../containers/_components/PropertyCode';
import { MobileFooter } from '../../lab/MobileFilterView';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';

export default function PropertyFilter({
  property,
  showLabels,
  placeholderPropertyKey,
  setEnableAriaHiddenFooter,
  setCloseButtonFocus,
  closeButtonFocus,
  columnSettings,
  showEmptyInputLabel,
  propertyContainerSetting,
  emptyInputLabelSettings,
  components,
  isMobile = false,
  data,
  isLoading,
  onChange,
  onClear,
  getNavigation,
  footerContainer = {},
  buttonsContainer = {},
  clearButtonSettings,
  doneButtonSettings,
  navigation,
  ...props
}) {
  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);
  const doneButtonRef = React.useRef(null);
  const locationSelectorRef = React.useRef(null);
  const [selectedValue, setSelectedValue] = React.useState(null);
  const [activateDoneButton, setActivateDoneButton] = React.useState(false);

  function renderItem(item) {
    return <PropertyCode item={item} {...components.listItem} />;
  }

  useEffect(() => {
    if (
      isMobile &&
      navigation === 'all' &&
      ((property.value && property.value !== selectedValue) ||
        property.value === undefined ||
        property.value === '')
    ) {
      locationSelectorRef.current?.update(property.value);
      setSelectedValue(property.value);
      setActivateDoneButton(false);
    }
  }, [navigation, isMobile, property, selectedValue]);

  const renderFooter = () => (
    <MobileFooter align="end" sx={{ width: '100%', right: 0, p: '10px' }}>
      <Inline gap={2} align="end">
        <Button
          type="button"
          variant="text"
          disabled={!Boolean(selectedValue)}
          onClick={() => {
            setSelectedValue(null);
            locationSelectorRef?.current?.onClear();
            setActivateDoneButton(true);
            doneButtonRef?.current?.focus();
          }}
          {...clearButtonSettings}
        >
          {translate('cmp-property-filter-clear-button', 'Clear')}
        </Button>
        <Button
          ref={doneButtonRef}
          disabled={!Boolean(selectedValue) && !activateDoneButton}
          onClick={() => {
            if (selectedValue && selectedValue !== property.value) {
              onChange(selectedValue);
            } else if (selectedValue === null) {
              onClear();
            }
            setActivateDoneButton(false);
            props.goBack();
          }}
          type="button"
          variant="text"
          {...doneButtonSettings}
        >
          {translate('cmp-property-filter-done-button', 'Done')}
        </Button>
      </Inline>
    </MobileFooter>
  );

  const renderPropertyLocationSelector = (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <LocationSelector
          name="Property"
          flex="1"
          showLabel={showLabels}
          {...components}
          defaultValue={property.value}
          options={data}
          loading={isLoading}
          itemRenderer={renderItem}
          dataTestId="locationSelector-property"
          placeholder={translate(placeholderPropertyKey, 'Type property name')}
          isMobile={isMobile}
          {...getNavigation}
          onChange={(e) => {
            if (isMobile) {
              setSelectedValue(e);
            } else {
              onChange(e);
            }
          }}
          onClear={(e) => {
            if (isMobile) {
              setSelectedValue(null);
              setActivateDoneButton(true);
            } else {
              onClear(e);
            }
          }}
          ref={locationSelectorRef}
        />
        {(property.value === '' || property.value === undefined) &&
          (showEmptyInputLabel ?? false) && (
            <Text {...emptyInputLabelSettings}>
              {translate('cmp-origin-empty-input-label', 'Input origin')}
            </Text>
          )}
        {navigation === 'Property' && renderFooter()}
      </ThemeProvider>
    </EmotionCache>
  );

  return (
    <Column display="flex" {...columnSettings}>
      <>
        {showEmptyInputLabel ?? false ? (
          <Box {...propertyContainerSetting}>{renderPropertyLocationSelector}</Box>
        ) : (
          renderPropertyLocationSelector
        )}
      </>
    </Column>
  );
}

PropertyFilter.propTypes = {
  showLabels: PropTypes.bool,
  property: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  }),
  placeholderPropertyKey: PropTypes.string
};

PropertyFilter.defaultProps = {
  showLabels: false,
  property: {
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  },
  placeholderPropertyKey: 'cmp-property-placeholder'
};
